import { useState, useEffect, useMemo } from 'react'
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material'
import { Page } from 'Common/Page/Page'
import { PageHeader, Dialog } from 'Common/Supplyvue-ui'
import {
  useCloneOperationalPlan,
  useDeleteOperationalPlan,
  useOperationalPlan,
  useUpdateOperationalPlan,
  useRunPlan,
  useUpdatePlanAccess,
} from './OperationalPlan.data'
import {
  PlanAccessType,
  PlanTableRow,
  TabPanelProps,
} from './OperationalPlanner.types'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { OperationalPlannerRenameModal } from './OperationalPlannerRename.modal'
import { OperationalPlannerCloneModal } from './OperationalPlannerClone.modal'
import {
  OPERTAUIONAL_PLAN_STATUS,
  WORKCENTRE_STATUS,
} from 'Common/Constants/Constants'
import {
  RunPlanParameters,
  RunPlanVariables,
} from 'Features/EditSettings/EditSettings.types'
import { getUserEmail } from 'Features/Auth/auth'
import { OperationalPlanDownloadModal } from 'Features/WorkCentreSummary/OperationalPlanDownload.modal'
import { ScenarioPlanView } from './ScenarioPlan.view'
import { OperationalPlanView } from './OperationalPlan.view'
import { SOROperationalPlanView } from './SystemOfRecords/SOROparationalPlans.view'
import { useTheme } from '@mui/material'
import { RightElement } from './RightElement.view'

const TABS = {
  PLANS_TABLE: 'plans-table',
  PLANNING_SCENARIO: 'planning-scenario',
  PUBLISHED_PLANS_TABLE: 'published-plans-table',
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ paddingTop: 2 }}>{children}</Box>}
    </Box>
  )
}

const CantPerformDialogContent = () => {
  return (
    <Box>
      <Typography>
        This action cant be performed. Action can only be performed on plans
        which have run successfully.
      </Typography>
    </Box>
  )
}

const DialogContent = ({
  planNameSelected,
}: {
  planNameSelected: string | null
}) => {
  return (
    <Box>
      <Typography>
        Delete action will discard all the plan results, so won&apos;t be able
        to view the plan results. Do you still want to delete the plan -{' '}
        {planNameSelected}?
      </Typography>
    </Box>
  )
}

export const OperationalPlannerPage = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { data } = useOperationalPlan('operational-plans')
  const {
    operationalPlanData,
    planningScenarioAccess,
    reportsAccess,
    plannedProductionAccess,
  } = data ?? {}

  const [showDeletePlanDialog, setShowDeletePlanDialog] = useState(false)
  const [planIdSelected, setPlanIdSelected] = useState<null | string>(null)
  const [planNameSelected, setPlanNameSelected] = useState<null | string>(null)
  const [planRenameModalOpen, setPlanRenameModalOpen] = useState(false)
  const [planCloneModalOpen, setPlanCloneModalOpen] = useState(false)
  const [showActionCantPerformDialogm, setShowActionCantPerformDialogm] =
    useState(false)
  const [userEmail, setUserEmail] = useState<null | string>(null)

  const [selectedTab, setSelectedTab] = useState(TABS.PLANS_TABLE)

  useEffect(() => {
    // This is a workaround for bread crumb coming to home page with /plan in the URL.
    navigate('/')
  }, [])

  const planType =
    selectedTab === TABS.PLANS_TABLE ? 'operational-plans' : 'scenario-plans'

  useEffect(() => {
    if (userEmail === null) {
      getUserEmail().then((email: string) => {
        setUserEmail(email ? email.toLocaleLowerCase() : '')
      })
    }
    return () => {
      setUserEmail(null)
    }
  }, [])

  /************ Delete Plan */
  const onDeleteSuccess = () => {
    setPlanNameSelected(null)
    toast.success('Plan deletion intiated')
  }

  const onDeleteFailure = () => {
    setPlanNameSelected(null)
    toast.error('Plan deletion failed')
  }

  const { mutate: deleteOperationalPlan } = useDeleteOperationalPlan({
    onDeleteSuccess,
    onDeleteFailure,
  })

  const handleDeletePlan = (
    planId: string,
    planName: string,
    status: string
  ) => {
    setPlanNameSelected(planName)
    if (status != 'INITIATED' && status != 'RUNNING') {
      setPlanIdSelected(planId)
      setShowDeletePlanDialog(true)
      return
    }
    setShowActionCantPerformDialogm(true)
  }

  const handleDeleteOkay = () => {
    setShowDeletePlanDialog(false)
    const planId = planIdSelected as string
    deleteOperationalPlan({ planType, planId })
    setPlanIdSelected(null)
  }
  /************ Delete plan end */
  /***********Rename */
  const onUpdateSuccess = () => {
    toast.success('Name updated successfully.')
    setPlanRenameModalOpen(false)
    setPlanIdSelected(null)
    setPlanNameSelected(null)
  }

  const onUpdateFailure = () => {
    toast.error('Name updated failed.')
    setPlanRenameModalOpen(false)
    setPlanIdSelected(null)
    setPlanNameSelected(null)
  }

  const { mutate: updateOperationalPlan, isLoading: isRenamingInProgress } =
    useUpdateOperationalPlan({
      onUpdateSuccess,
      onUpdateFailure,
    })

  const handleRenameClick = (newName: string) => {
    updateOperationalPlan({
      planId: planIdSelected as string,
      planReName: newName,
    })
  }

  const handleRenamePlan = (
    planId: string,
    planName: string,
    status: string
  ) => {
    if (status === WORKCENTRE_STATUS.SUCCESS) {
      setPlanIdSelected(planId)
      setPlanNameSelected(planName)
      setPlanRenameModalOpen(true)
      return
    }
    setShowActionCantPerformDialogm(true)
  }

  /***********End plan rename */

  /************* Lock/unlock plan */

  const onSwicthLockSuccess = () => {
    toast.success('Updating plan access success.')
  }
  const onSwitchLockFail = () => {
    toast.error('Changing plan access type failed')
  }
  const { mutate: updatePlanAccesssType } = useUpdatePlanAccess({
    onSwicthLockSuccess,
    onSwitchLockFail,
  })
  const handlePlanAccessChange = (
    planId: string,
    planAccessType: PlanAccessType
  ) => {
    updatePlanAccesssType({ planId, planAccessType, planType })
  }

  /***********End lock/unlock */

  const handleRowClick = (row: PlanTableRow) => {
    try {
      const { planId, status } = row
      if (
        status === 'SUCCESS' ||
        status === OPERTAUIONAL_PLAN_STATUS.PUBLISHED
      ) {
        navigate(`/plan/${planId}`)
      } else if (status === 'PLAN_DATA_LOADED') {
        navigate(`/plan/${planId}/edit`)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleDialogClose = () => {
    setShowDeletePlanDialog(false)
    setPlanIdSelected(null)
  }
  const handleCantPerformDialogClose = () => {
    setShowActionCantPerformDialogm(false)
    setPlanIdSelected(null)
  }

  /***************Clone */
  const onCloneSuccess = () => {
    setPlanIdSelected(null)
    toast.success('Cloning operational plan successfully initiated.')
  }
  const onCloneFailure = () => {
    setPlanIdSelected(null)
    toast.error('Cloning operational plan failed.')
  }
  const { mutate: cloneOperationalPlan } = useCloneOperationalPlan({
    onCloneSuccess,
    onCloneFailure,
  })

  const onPlanRunInitiatedSucces = () => {
    toast.success('Plan run initiated.')
  }

  const onPlanRunInitiatedError = (message: string) => {
    toast.error(message)
  }

  const { mutate: run } = useRunPlan(
    onPlanRunInitiatedSucces,
    onPlanRunInitiatedError
  )

  const handleClonePlan = (
    planId: string,
    planName: string,
    status: string
  ) => {
    if (
      status === WORKCENTRE_STATUS.SUCCESS ||
      status === WORKCENTRE_STATUS.PUBLISHED
    ) {
      setPlanIdSelected(planId)
      setPlanNameSelected(planName)
      setPlanCloneModalOpen(true)
      return
    }
    setShowActionCantPerformDialogm(true)
  }

  const handleCloneClick = (newPlanName: string) => {
    setPlanCloneModalOpen(false)
    cloneOperationalPlan({
      planId: planIdSelected as string,
      planName: newPlanName,
      planType,
    })
  }

  const handleEditPlan = (planId: string) => {
    navigate(`/plan/${planId}/edit`)
  }

  const handleRunPlan = (planId: string) => {
    const runPlanParams: RunPlanParameters = {
      extractData: false,
      unconstrainedPlan: true,
    }

    const runPlanVariables: RunPlanVariables = {
      planId: planId,
      parameters: runPlanParams,
    }
    run(runPlanVariables)
  }

  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleDownloadPlan = (
    planId: string,
    planName: string,
    status: string
  ) => {
    if (status === WORKCENTRE_STATUS.SUCCESS) {
      setPlanIdSelected(planId)
      setPlanNameSelected(planName)
      setIsModalOpen(true)
      return
    }
    setShowActionCantPerformDialogm(true)
  }

  const nameList: string[] = useMemo(() => {
    if (operationalPlanData) {
      return operationalPlanData?.map(
        (planData: PlanTableRow) => planData.planName
      )
    } else {
      return []
    }
  }, [operationalPlanData])

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader
          title="Operational plans"
          rightElement={
            <RightElement
              planningScenarioAccess
              reportsAccess={reportsAccess}
              plannedProductionAccess={plannedProductionAccess}
              bgColor={theme.palette.primary.main}
            />
          }
        />
        <Box>
          <Paper>
            <Tabs
              value={selectedTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => setSelectedTab(newValue)}
            >
              <Tab label="Plans" value={TABS.PLANS_TABLE} />
              <Tab label="Published plans" value={TABS.PUBLISHED_PLANS_TABLE} />
              {planningScenarioAccess === true && (
                <Tab
                  label="Planning scenarios"
                  value={TABS.PLANNING_SCENARIO}
                />
              )}
            </Tabs>
          </Paper>
        </Box>
        <Box>
          <TabPanel value={selectedTab} index={TABS.PLANS_TABLE}>
            <OperationalPlanView
              userEmail={userEmail as string}
              handleRowClick={handleRowClick}
              handleRenamePlan={handleRenamePlan}
              handleDeletePlan={handleDeletePlan}
              handleClonePlan={handleClonePlan}
              handleEditPlan={handleEditPlan}
              handleRunPlan={handleRunPlan}
              handleDownloadPlan={handleDownloadPlan}
              handlePlanAccessChange={handlePlanAccessChange}
            />
          </TabPanel>
          <TabPanel value={selectedTab} index={TABS.PUBLISHED_PLANS_TABLE}>
            <SOROperationalPlanView />
          </TabPanel>
          {planningScenarioAccess === true && (
            <TabPanel value={selectedTab} index={TABS.PLANNING_SCENARIO}>
              <ScenarioPlanView
                userEmail={userEmail as string}
                handleRowClick={handleRowClick}
                handleRenamePlan={handleRenamePlan}
                handleDeletePlan={handleDeletePlan}
                handleClonePlan={handleClonePlan}
                handleEditPlan={handleEditPlan}
                handleRunPlan={handleRunPlan}
                handleDownloadPlan={handleDownloadPlan}
                handlePlanAccessChange={handlePlanAccessChange}
              ></ScenarioPlanView>
            </TabPanel>
          )}
        </Box>
      </Box>
      <Dialog
        title={`Delete plan - ${planNameSelected}`}
        content={<DialogContent planNameSelected={planNameSelected} />}
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        open={showDeletePlanDialog}
        handleClose={handleDialogClose}
        handlePrimaryButtonClick={handleDeleteOkay}
        handleSecondaryButtonClick={handleDialogClose}
      />
      <Dialog
        title="Can't perform this action."
        content={<CantPerformDialogContent />}
        primaryButtonLabel="Ok"
        open={showActionCantPerformDialogm}
        handleClose={handleCantPerformDialogClose}
        handlePrimaryButtonClick={handleCantPerformDialogClose}
      />
      <OperationalPlannerRenameModal
        planName={planNameSelected ?? ''}
        isModalOpen={planRenameModalOpen}
        handleModalClose={() => setPlanRenameModalOpen(false)}
        handleRenameClick={handleRenameClick}
        isLoading={isRenamingInProgress}
        nameList={nameList}
      />
      <OperationalPlannerCloneModal
        planName={planNameSelected ?? ''}
        isModalOpen={planCloneModalOpen}
        handleModalClose={() => setPlanCloneModalOpen(false)}
        handleCloneClick={handleCloneClick}
        isLoading={isRenamingInProgress}
        nameList={nameList}
      />
      {isModalOpen && (
        <OperationalPlanDownloadModal
          isModalOpen={isModalOpen}
          handleModalCancel={() => setIsModalOpen(false)}
          planId={planIdSelected as string}
          selectedPlanName={planNameSelected ?? ''}
        />
      )}
    </Page>
  )
}
